import { FUNCTION_WHITELIST, isReservedVariableName } from './constants';
function ok(value) {
    return { ok: true, value };
}
function fail(error, message) {
    return { ok: false, error, message };
}
function toNumber(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    return null;
}
function toBoolean(v) {
    if (typeof v === 'boolean')
        return v;
    if (v === null)
        return false;
    if (typeof v === 'number')
        return v !== 0;
    if (typeof v === 'string')
        return v.length > 0;
    return false;
}
function equalsLoose(a, b) {
    if (a === null || b === null)
        return a === b;
    if (typeof a === typeof b)
        return a === b;
    const an = toNumber(a);
    const bn = toNumber(b);
    if (an !== null && bn !== null)
        return an === bn;
    return String(a) === String(b);
}
function pickAnswerValue(raw) {
    if (raw === undefined)
        return null;
    if (Array.isArray(raw)) {
        return raw.length === 0 ? null : (raw[0] ?? null);
    }
    return raw;
}
function compare(op, left, right) {
    switch (op) {
        case 'eq':
            return ok(equalsLoose(left, right));
        case 'neq':
            return ok(!equalsLoose(left, right));
        case 'lt':
        case 'gt':
        case 'lte':
        case 'gte': {
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (ln === null || rn === null) {
                return fail('TYPE_MISMATCH', `comparison ${op} requires numeric operands`);
            }
            if (op === 'lt')
                return ok(ln < rn);
            if (op === 'gt')
                return ok(ln > rn);
            if (op === 'lte')
                return ok(ln <= rn);
            return ok(ln >= rn);
        }
        case 'in':
        case 'not_in': {
            if (typeof right !== 'string') {
                return fail('TYPE_MISMATCH', `${op} requires the right side to be a comma-delimited string`);
            }
            const haystack = right.split(',').map((s) => s.trim());
            const needle = left === null ? '' : String(left);
            const present = haystack.includes(needle);
            return ok(op === 'in' ? present : !present);
        }
        default:
            return fail('UNKNOWN_EXPRESSION_TYPE', `unknown comparison op: ${String(op)}`);
    }
}
function arithmetic(op, left, right) {
    const ln = toNumber(left);
    const rn = toNumber(right);
    if (ln === null || rn === null) {
        return fail('TYPE_MISMATCH', `arithmetic ${op} requires numeric operands`);
    }
    switch (op) {
        case 'add':
            return ok(ln + rn);
        case 'sub':
            return ok(ln - rn);
        case 'mul':
            return ok(ln * rn);
        case 'div':
            if (rn === 0)
                return fail('DIVIDE_BY_ZERO', 'division by zero');
            return ok(ln / rn);
    }
}
function callFunction(name, args, ctx) {
    if (!FUNCTION_WHITELIST.has(name)) {
        return fail('UNKNOWN_FUNCTION', `function not in whitelist: ${name}`);
    }
    switch (name) {
        case 'sum':
        case 'avg':
        case 'min':
        case 'max':
        case 'count': {
            const nums = [];
            for (const a of args) {
                const n = toNumber(a);
                if (n !== null)
                    nums.push(n);
            }
            if (name === 'count')
                return ok(nums.length);
            if (nums.length === 0)
                return ok(null);
            if (name === 'sum')
                return ok(nums.reduce((s, x) => s + x, 0));
            if (name === 'avg')
                return ok(nums.reduce((s, x) => s + x, 0) / nums.length);
            if (name === 'min')
                return ok(Math.min(...nums));
            return ok(Math.max(...nums));
        }
        case 'length': {
            if (args.length !== 1)
                return fail('ARITY_MISMATCH', 'length(x) takes 1 arg');
            const v = args[0];
            if (typeof v === 'string')
                return ok(v.length);
            if (v === null)
                return ok(0);
            return fail('TYPE_MISMATCH', 'length() requires a string argument');
        }
        case 'upper':
        case 'lower':
        case 'trim': {
            if (args.length !== 1)
                return fail('ARITY_MISMATCH', `${name}(x) takes 1 arg`);
            const v = args[0];
            if (typeof v !== 'string') {
                return fail('TYPE_MISMATCH', `${name}() requires a string argument`);
            }
            if (name === 'upper')
                return ok(v.toUpperCase());
            if (name === 'lower')
                return ok(v.toLowerCase());
            return ok(v.trim());
        }
        case 'now': {
            if (args.length !== 0)
                return fail('ARITY_MISMATCH', 'now() takes 0 args');
            const d = ctx.now ?? new Date();
            return ok(d.toISOString());
        }
    }
}
export function evaluate(expr, ctx) {
    switch (expr.type) {
        case 'literal':
            return ok(expr.value);
        case 'answer': {
            const raw = ctx.answers[expr.questionCode];
            if (raw === undefined) {
                return fail('MISSING_ANSWER', `answer not found: ${expr.questionCode}`);
            }
            return ok(pickAnswerValue(raw));
        }
        case 'variable': {
            if (!(expr.name in ctx.variables)) {
                return fail('MISSING_VARIABLE', `variable not set: ${expr.name}`);
            }
            return ok(ctx.variables[expr.name]);
        }
        case 'reserved': {
            if (!isReservedVariableName(expr.name)) {
                return fail('UNKNOWN_RESERVED', `unknown reserved variable: ${String(expr.name)}`);
            }
            return ok((ctx.now ?? new Date()).toISOString());
        }
        case 'comparison': {
            const l = evaluate(expr.left, ctx);
            if (!l.ok)
                return l;
            const r = evaluate(expr.right, ctx);
            if (!r.ok)
                return r;
            return compare(expr.op, l.value, r.value);
        }
        case 'arithmetic': {
            const l = evaluate(expr.left, ctx);
            if (!l.ok)
                return l;
            const r = evaluate(expr.right, ctx);
            if (!r.ok)
                return r;
            return arithmetic(expr.op, l.value, r.value);
        }
        case 'logical': {
            const l = evaluate(expr.left, ctx);
            if (!l.ok)
                return l;
            if (expr.op === 'and' && !toBoolean(l.value))
                return ok(false);
            if (expr.op === 'or' && toBoolean(l.value))
                return ok(true);
            const r = evaluate(expr.right, ctx);
            if (!r.ok)
                return r;
            return ok(toBoolean(r.value));
        }
        case 'not': {
            const v = evaluate(expr.expr, ctx);
            if (!v.ok)
                return v;
            return ok(!toBoolean(v.value));
        }
        case 'if': {
            const c = evaluate(expr.cond, ctx);
            if (!c.ok)
                return c;
            return toBoolean(c.value) ? evaluate(expr.then, ctx) : evaluate(expr.else, ctx);
        }
        case 'function': {
            const evaluated = [];
            for (const arg of expr.args) {
                const r = evaluate(arg, ctx);
                if (!r.ok)
                    return r;
                evaluated.push(r.value);
            }
            return callFunction(expr.name, evaluated, ctx);
        }
        default: {
            const exhaustive = expr;
            return fail('UNKNOWN_EXPRESSION_TYPE', `unknown expression type: ${JSON.stringify(exhaustive)}`);
        }
    }
}
export function collectQuestionRefs(expr, into) {
    switch (expr.type) {
        case 'literal':
        case 'variable':
        case 'reserved':
            return;
        case 'answer':
            into.add(expr.questionCode);
            return;
        case 'not':
            collectQuestionRefs(expr.expr, into);
            return;
        case 'if':
            collectQuestionRefs(expr.cond, into);
            collectQuestionRefs(expr.then, into);
            collectQuestionRefs(expr.else, into);
            return;
        case 'comparison':
        case 'arithmetic':
        case 'logical':
            collectQuestionRefs(expr.left, into);
            collectQuestionRefs(expr.right, into);
            return;
        case 'function':
            for (const a of expr.args)
                collectQuestionRefs(a, into);
            return;
    }
}
export function collectVariableRefs(expr, into) {
    switch (expr.type) {
        case 'literal':
        case 'answer':
        case 'reserved':
            return;
        case 'variable':
            into.add(expr.name);
            return;
        case 'not':
            collectVariableRefs(expr.expr, into);
            return;
        case 'if':
            collectVariableRefs(expr.cond, into);
            collectVariableRefs(expr.then, into);
            collectVariableRefs(expr.else, into);
            return;
        case 'comparison':
        case 'arithmetic':
        case 'logical':
            collectVariableRefs(expr.left, into);
            collectVariableRefs(expr.right, into);
            return;
        case 'function':
            for (const a of expr.args)
                collectVariableRefs(a, into);
            return;
    }
}
export function collectFunctionRefs(expr, into) {
    switch (expr.type) {
        case 'literal':
        case 'answer':
        case 'variable':
        case 'reserved':
            return;
        case 'not':
            collectFunctionRefs(expr.expr, into);
            return;
        case 'if':
            collectFunctionRefs(expr.cond, into);
            collectFunctionRefs(expr.then, into);
            collectFunctionRefs(expr.else, into);
            return;
        case 'comparison':
        case 'arithmetic':
        case 'logical':
            collectFunctionRefs(expr.left, into);
            collectFunctionRefs(expr.right, into);
            return;
        case 'function':
            into.add(expr.name);
            for (const a of expr.args)
                collectFunctionRefs(a, into);
            return;
    }
}
